/**
* @author 贝才[beica1@outook.com]
* @date 2021/4/28
* @description
*   TheChatMessage.vue of WeTrade
*/
<template>
  <div class="p-16 msg" :class="{rtl: isSelf}">
    <div class="avatar order" :class="{halo: isMaster}">
      <Pic :src="custom.avatar" fallback="/img/user/Avatar@2x.png" height="32" width="32" class="round" />
    </div>
    <div class="order mx-12 pt-8" style="max-width: 70%">
      <div style="overflow: hidden">
        <p class="order f-md c-text f-400">{{msg.isSelf ? 'Me' : msg.fromNick}}</p>
        <span
          v-if="custom.level !== undefined"
          class="order cut-diamond f-sm mx-6" style="text-align: center"
          :class="{meta: custom.level !== 0}"
        >
          {{custom.level}}
        </span>
      </div>
      <Time format="HH:mm:ss" tag="p" class="f-xs c-tip" :time="msg.time" />
      <div class="content t-left" :class="{self: isSelf, master: isMaster}">
        <span class="bubble"></span>
        <p style="word-break: break-all;" class="c-text" v-html="renderEmoji(msg.text)" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { parseToJson } from '@/common/format'
import Pic from '@/components/Pic.vue'
import Time from '@/components/Time.vue'
import { renderEmoji } from '@/modules/community/communities/community'
import { ChatConfig } from '@/modules/membership/member.api'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'TheChatMessage',
  components: { Time, Pic },
  props: {
    msg: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
  },
  setup (props) {
    const custom = parseToJson(props.msg.custom).json ?? {}
    const isSelf = (props.config as ChatConfig).accId === props.msg.from
    return {
      isSelf,
      isMaster: !isSelf && custom.isMaster,
      custom,
      renderEmoji,
    }
  },
})
</script>

<style scoped lang="scss">
.msg {
  overflow: hidden;
}

.avatar {
  display: inline-block;
  padding: 8px 0;

  img {
    vertical-align: middle;
  }

  &.halo {
    background: url(/img/membership/halo@2x.png) center no-repeat;
    background-size: 100%;
  }
}

.content {
  margin: 8px 12px 0 12px;
  position: relative;
  border-radius: 8px;
  padding: 10px;
  background: var(--color-light);

  &.self {
    background: #98B5FE;

    .bubble::before,
    .bubble::after {
      border-color: #98B5FE;
    }
  }

  &.master {
    background: #FCE9D6;

    .bubble::before,
    .bubble::after {
      border-color: #FCE9D6;
    }
  }
}

.bubble {
  z-index: -1;
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  vertical-align: middle;
  width: 16px;
  height: 16px;

  &::before {
    content: "";
    position: absolute;
    top: -12px;
    right: -4px;
    height: 30px;
    border-left: 32px solid var(--color-light);
    background: var(--color-light);
    border-bottom-left-radius: 40px 30px;
  }

  &::after {
    content: "";
    position: absolute;
    top: -30px;
    right: -4px;
    width: 34px;
    height: 30px;
    background: white;
    border-bottom-left-radius: 20px 10px;
  }
}

.order {
  float: left;
}

.rtl {
  .order {
    float: right;
    text-align: right;
  }

  .bubble {
    left: unset;
    right: 0;
    transform: rotateY(180deg);
  }
}

.cut-diamond {
  color: white;
  border-style: solid;
  border-color: transparent transparent var(--color-tip) transparent;
  border-width: 0 6px 6px 6px;
  top: 2px;
  height: 0;
  width: 8px;
  box-sizing: content-box;
  position: relative;

  &.meta {
    border-bottom-color: var(--color-primary);

    &::after {
      border-top-color: var(--color-primary);
    }
  }
}

.cut-diamond::after {
  z-index: -1;
  content: "";
  position: absolute;
  top: 6px;
  left: -6px;
  width: 0;
  height: 0;
  border-style: solid;
  border-color: var(--color-tip) transparent transparent transparent;
  border-width: 10px 10px 0 10px;
}
.text-nowrap{
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.f-400{
  font-weight: 400;
}
</style>
